import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, FlaskConical, Ban, Eye, RefreshCw, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { ChannelChip } from '~/components/common/chips';
import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { AnalyticsSyncDialog } from '~/features/analytics-sync/components/analytics-sync-dialog';
import { useAnalyticsSyncStore } from '~/features/analytics-sync/store/analytics-sync.store';
import { ArchiveHistoryPopover } from '~/features/list-archive/components/archive-history-popover';
import { ArchivedBadge } from '~/features/list-archive/components/archived-badge';
import { useCanManageArchive } from '~/features/list-archive/hooks/use-can-manage-archive';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import { cn } from '~/utils/cn';
import { formatAuditTimestamp } from '~/utils/date';

// import { PipelineStatus } from '../../services/experiment-pipeline.service';
import {
  ExperimentPlatform,
  ExperimentStatus,
  type ExperimentDetail,
  type ExperimentSegmentOption,
} from '../../types/experiment.types';
import { ExperimentArchiveControl } from '../experiment-archive-control';
import { ExperimentStatusChip } from '../experiment-status-chip';

import { RestartExperimentSheet } from './restart-experiment-sheet';
import { RetryPipelineDialog } from './retry-pipeline-dialog';
import { StopExperimentDialog } from './stop-experiment-dialog';

/** Maps the API's lowercase platform enum to the casing the shared
 *  `ChannelChip`/`PLATFORM_LOGO` lookup expects. */
const PLATFORM_DISPLAY: Record<ExperimentPlatform, string> = {
  [ExperimentPlatform.GOOGLE]: 'Google',
  [ExperimentPlatform.META]: 'Meta',
};

export type ExperimentDetailTab = 'timeline' | 'details' | 'products';

interface Props {
  experiment: ExperimentDetail;
  activeTab: ExperimentDetailTab;
  segment: string;
  onSegmentChange: (value: string) => void;
  /**
   * Suppress the segment dropdown, Stop CTA, and Tabs strip — the
   * "delivery in progress" state is intentionally chrome-light so the
   * `DeliveryInProgressCard` reads as the focus of the screen.
   */
  isDelivering?: boolean;
  /**
   * Latest pipeline-workflow status. `FAILED` surfaces the Retry CTAs
   * (full restart / delivery-only); `null` before the status query resolves.
   */
  // pipelineStatus?: PipelineStatus | null;
}

export function ExperimentDetailHeader({
  experiment,
  activeTab,
  segment,
  onSegmentChange,
  isDelivering = false,
  // pipelineStatus = null,
}: Props) {
  const { t } = useTranslation('experiments');
  // Shared actor labels ("Created by" / "Last action by") live in the common ns.
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  // Restart is gated behind the `restart-experiment` flag (inline UI, no route).
  const { isAllowed: isRestartAllowed } = useFeatureGate('restart-experiment');
  // List Archive: super / org admins can archive a finished experiment from
  // here, or unarchive one they deep-linked into. The control patches the cached detail.
  const canManageArchive = useCanManageArchive();
  const activeTabLabel = t(`detail.tabs.${activeTab}`);
  const [stopOpen, setStopOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [retryMode, setRetryMode] = useState<'full' | 'delivery-only' | null>(null);
  // Block a second sync for this experiment while one is still registered in
  // the global store; selecting the boolean keeps the button reactive to
  // session start/end without re-rendering on unrelated store changes.
  const hasActiveSync = useAnalyticsSyncStore((s) => experiment.id in s.sessions);
  // Experiment start date (proxy: today − runningSinceDays) — lower bound of
  // the analytics-sync date picker. Memoised so the dialog's bounds are stable.
  const experimentStartDate = useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() - experiment.runningSinceDays);
    return start;
  }, [experiment.runningSinceDays]);
  // Per the State-Transitions spec UX query: "Should the /stop button be
  // enabled? status NOT IN (CANCELLED, COMPLETED, REJECTED)." Pre-launch DRAFT /
  // CREATED states are also hidden — there's nothing running yet to stop.
  // REJECTED is terminal (operator declined at approval gate — BUG-029).
  // `isDelivering` suppresses the CTA mid-launch (chrome-light delivery state).
  const canStop =
    !isDelivering &&
    experiment.status !== ExperimentStatus.CANCELLED &&
    experiment.status !== ExperimentStatus.REJECTED &&
    experiment.status !== ExperimentStatus.COMPLETED &&
    experiment.status !== ExperimentStatus.DRAFT;
  // Restart clones this experiment's config into a brand-new run (the original
  // is left untouched). Offered for every status that reaches the detail
  // dashboard — i.e. anything past the funnel (not DRAFT / CREATED). The action
  // row is already suppressed mid-delivery via `isDelivering`.
  const canRestart =
    isRestartAllowed &&
    experiment.status !== ExperimentStatus.DRAFT &&
    experiment.status !== ExperimentStatus.CREATED;
  // A FAILED pipeline surfaces the Retry CTAs (full restart + delivery-only).
  // FAILED is never an in-flight state, so the `isDelivering` guard is just
  // kept symmetric with the Stop CTA above.
  // const canRetry = !isDelivering && pipelineStatus === PipelineStatus.FAILED;

  // Options: "All Segments" always first; when the experiment was scoped to a
  // product group (`groupingFilter.name` set), add the group itself and its
  // complement ("Remaining products") so the operator can isolate either half.
  const segmentOptions: ExperimentSegmentOption[] = (() => {
    const allSegments: ExperimentSegmentOption = { value: 'all', label: t('detail.allSegments') };
    if (!experiment.groupingFilter?.name) {
      return [allSegments];
    }
    return [
      allSegments,
      { value: 'group', label: experiment.groupingFilter.name },
      { value: 'remaining', label: t('detail.remainingProducts') },
    ];
  })();

  return (
    <div className="space-y-4">
      {/* Row 1: Breadcrumbs (left) + Segment dropdown + filter icon (right) */}
      <div className="flex items-center justify-between gap-4">
        <nav
          aria-label="Breadcrumb"
          className="text-muted-foreground flex items-center gap-1.5 text-sm"
        >
          <FlaskConical className="h-4 w-4" aria-hidden="true" />
          <Link to="/experiments" className="hover:text-foreground transition-colors">
            {t('detail.breadcrumbRoot')}
          </Link>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
          <span className={isDelivering ? 'text-foreground font-medium' : undefined}>
            {experiment.name}
          </span>
          {!isDelivering && (
            <>
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
              <span className="text-foreground font-medium">{activeTabLabel}</span>
            </>
          )}
        </nav>

        {!isDelivering && (
          <div className="flex items-center gap-2">
            <label htmlFor="experiment-segment" className="text-foreground text-sm font-medium">
              {t('detail.segment')}
            </label>
            <Select value={segment} onValueChange={onSegmentChange}>
              <SelectTrigger id="experiment-segment" className="w-[220px] cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {segmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Row 2: tabs & action buttons — always inline, never wraps. With three
          action buttons the labels only fit at ≥1280px, so each label is
          icon-only below 1280px and shown alongside the icon at 1280px and up.
          A tooltip covers the icon-only band. */}
      {!isDelivering && (
        <div className="flex items-center justify-between gap-5">
          <TabsList className="bg-white">
            <TabsTrigger value="timeline" className="flex-1 cursor-pointer">
              {t('detail.tabs.timeline')}
            </TabsTrigger>
            <TabsTrigger value="details" className="flex-1 cursor-pointer">
              {t('detail.tabs.details')}
            </TabsTrigger>
            <TabsTrigger value="products" className="flex-1 cursor-pointer">
              {t('detail.tabs.products')}
            </TabsTrigger>
          </TabsList>

          {/* action buttons */}
          <div className="flex shrink-0 items-center gap-4">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="lg"
                  aria-label={t('detail.syncAnalytics.trigger')}
                  disabled={hasActiveSync}
                  className="border-primary hover:text-primary cursor-pointer px-5 disabled:cursor-not-allowed"
                  onClick={() => {
                    setSyncOpen(true);
                  }}
                >
                  <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
                  <span className={cn('max-[1679px]:hidden', 'min-[1680px]:inline')}>
                    {t('detail.syncAnalytics.trigger')}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {hasActiveSync
                  ? t('detail.syncAnalytics.inProgress')
                  : t('detail.syncAnalytics.trigger')}
              </TooltipContent>
            </Tooltip>

            {canStop && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="lg"
                    aria-label={t('detail.stop.trigger')}
                    className="border-destructive hover:text-destructive cursor-pointer px-5"
                    onClick={() => {
                      setStopOpen(true);
                    }}
                  >
                    <Ban aria-hidden="true" className="text-destructive h-3.5 w-3.5" />
                    <span className={cn('max-[1679px]:hidden', 'min-[1680px]:inline')}>
                      {t('detail.stop.trigger')}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('detail.stop.trigger')}</TooltipContent>
              </Tooltip>
            )}

            {canRestart && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="lg"
                    aria-label={t('detail.restart.trigger')}
                    className="border-primary hover:text-primary cursor-pointer px-5"
                    onClick={() => {
                      setRestartOpen(true);
                    }}
                  >
                    <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
                    <span className={cn('max-[1679px]:hidden', 'min-[1680px]:inline')}>
                      {t('detail.restart.trigger')}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('detail.restart.trigger')}</TooltipContent>
              </Tooltip>
            )}

            {/* Admin-only Archive / Unarchive (List Archive). Same outline-lg
                treatment as its siblings; label hidden below 1680px. An in-flight
                sub-run opens the Stop dialog, then the archive retries. */}
            {canManageArchive && (
              <>
                <ExperimentArchiveControl
                  experimentId={experiment.id}
                  archivedAt={experiment.archivedAt}
                  buttonVariant="outline"
                  buttonSize="lg"
                  showLabel
                  className="border-primary hover:text-primary px-5"
                />
                {/* Every archive / restore of this experiment, newest first (detail only). */}
                <ArchiveHistoryPopover
                  history={experiment.archiveHistory}
                  className="border-primary hover:text-primary h-11 w-11"
                />
              </>
            )}

            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="lg"
                  aria-label={t('detail.viewBatchDetails.trigger')}
                  className="border-primary hover:text-primary cursor-pointer px-5"
                  onClick={() => {
                    void navigate({
                      to: '/catalog-operations/$batchId',
                      params: { batchId: experiment.id },
                      search: { tab: 'delivery-health', source: 'experiment' },
                    });
                  }}
                >
                  <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                  <span className={cn('max-[1679px]:hidden', 'min-[1680px]:inline')}>
                    {t('detail.viewBatchDetails.trigger')}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('detail.viewBatchDetails.trigger')}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Row 3: Title + platform chip (left) + Stop button (right) */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{experiment.name}</h1>
          {experiment.platform && (
            <ChannelChip
              platform={PLATFORM_DISPLAY[experiment.platform]}
              label={t(`platform.${experiment.platform}`)}
            />
          )}
          {/* Row 3: Stats + status chips */}
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
            <ExperimentStatusChip status={experiment.status} />
            {/* List Archive: says why a deep-linked experiment is missing from the default list. */}
            <ArchivedBadge archivedAt={experiment.archivedAt} archivedBy={experiment.archivedBy} />
            <span className="bg-primary/10 text-primary inline-flex items-center rounded-md px-3 py-2.5 text-xs font-medium">
              {t('detail.runningSince', { count: experiment.runningSinceDays })}
            </span>
          </div>
        </div>
        {/* {canRetry && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => {
                setRetryMode('full');
              }}
            >
              <RotateCcw aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
              {t('detail.retry.full.trigger')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => {
                setRetryMode('delivery-only');
              }}
            >
              {t('detail.retry.delivery.trigger')}
            </Button>
          </>
        )} */}
      </div>

      <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
        {`${
          experiment.skuCount === null
            ? t('detail.subtitleNoSkus', { armCount: experiment.armCount })
            : t('detail.subtitle', {
                skuCount: experiment.skuCount,
                armCount: experiment.armCount,
              })
        }${experiment.trafficSplit ? ` · (${experiment.trafficSplit.join('/')} split)` : ''}`}
      </div>

      {/* Provenance: who created the experiment and who last acted on it, from the
          workflow-service audit stamps. Last-action is shown only when present. */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="flex min-w-0 items-center gap-1.5">
          {tc('actor.createdBy')}
          <ActorStringLabel value={experiment.createdBy} showAvatar={false} className="text-xs" />
          <span aria-hidden>·</span>
          <span className="tabular-nums">{formatAuditTimestamp(experiment.createdAt)}</span>
        </span>
        {(Boolean(experiment.lastActionBy) || Boolean(experiment.lastActionAt)) && (
          <span className="flex min-w-0 items-center gap-1.5">
            {tc('actor.lastActionBy')}
            <ActorStringLabel
              value={experiment.lastActionBy}
              showAvatar={false}
              className="text-xs"
            />
            {experiment.lastActionAt && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">
                  {formatAuditTimestamp(experiment.lastActionAt)}
                </span>
              </>
            )}
          </span>
        )}
      </div>

      <StopExperimentDialog
        open={stopOpen}
        onOpenChange={setStopOpen}
        experimentId={experiment.id}
      />
      <RestartExperimentSheet
        open={restartOpen}
        onOpenChange={setRestartOpen}
        experiment={experiment}
      />
      <AnalyticsSyncDialog
        open={syncOpen}
        onOpenChange={setSyncOpen}
        experimentId={experiment.id}
        experimentName={experiment.name}
        experimentStartDate={experimentStartDate}
      />
      {retryMode !== null && (
        <RetryPipelineDialog
          open
          onOpenChange={(open) => {
            if (!open) setRetryMode(null);
          }}
          experimentId={experiment.id}
          mode={retryMode}
        />
      )}
    </div>
  );
}
